import { FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../contexts/OrgAuthContext'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import { orgAppClient, OrgAppClientError } from '../services/orgAppClient'

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refreshSession } = useOrgAuth()

  const urlMessage = searchParams.get('message')
  const urlError = searchParams.get('error')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  // Try to find a specific error translation, otherwise fallback to the URL message or standard error format
  const getInitialError = () => {
    if (urlError) {
      if (t(`login.error_${urlError}`) !== `login.error_${urlError}`) {
        return t(`login.error_${urlError}`)
      }
      return urlMessage || t('login.signInError', { error: urlError })
    }
    return null
  }

  const [error, setError] = useState<string | null>(getInitialError())
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Magic-link login: 'password' (default form) → 'magic' (email entry) → 'sent'
  const [mode, setMode] = useState<'password' | 'magic' | 'sent'>('password')
  const [magicEmail, setMagicEmail] = useState('')
  const [magicSending, setMagicSending] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const [sentMinutes, setSentMinutes] = useState(15)

  useEffect(() => {
    if (resendIn <= 0) return
    const id = setInterval(() => setResendIn((s) => s - 1), 1000)
    return () => clearInterval(id)
  }, [resendIn])

  const redirectTo = searchParams.get('redirect') || '/app'

  const describeAuthError = (err: unknown) => {
    const apiError = err as OrgAppClientError
    const overrideError = apiError.code
      ? t(`login.error_${apiError.code}`) !== `login.error_${apiError.code}`
        ? t(`login.error_${apiError.code}`)
        : null
      : null
    return (
      overrideError ||
      (apiError.message && apiError.message !== 'An error occurred'
        ? apiError.message
        : t('login.defaultSignInError'))
    )
  }

  const handlePasswordLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      await orgAppClient.login(email, password)
      await refreshSession()
      navigate(redirectTo, { replace: true })
    } catch (err) {
      setError(describeAuthError(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  const requestMagicLink = async () => {
    setError(null)
    setMagicSending(true)
    try {
      // The API is neutral by contract: always show the sent state.
      const res = (await orgAppClient.requestMagicLink(magicEmail, redirectTo)) as {
        expires_in_minutes?: number
      } | null
      setSentMinutes(res?.expires_in_minutes ?? 15)
      setMode('sent')
      setResendIn(60)
    } catch (err) {
      setError(describeAuthError(err))
    } finally {
      setMagicSending(false)
    }
  }

  const handleMagicRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await requestMagicLink()
  }

  return (
    <div className="min-h-[calc(100vh-9rem)] flex items-center justify-center bg-slate-50">
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-xl bg-primary-100 flex items-center justify-center">
                <img src="/logo.svg" alt="Logo" className="h-8 w-8" />
              </div>
              <div>
                <p className="text-sm text-slate-600">{t('login.erpAccess')}</p>
                <h1 className="text-xl font-semibold text-slate-900">{t('login.signInTitle')}</h1>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-sm text-red-600" role="alert">{error}</p>
              </div>
            )}

            {mode === 'password' && (
              <form className="space-y-4" onSubmit={handlePasswordLogin}>
                <Input
                  id="email"
                  label={t('login.workEmail')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Input
                  id="password"
                  label={t('login.password')}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('login.signingIn') : t('login.signIn')}
                </Button>

                <div className="flex items-center gap-3 pt-2" aria-hidden="true">
                  <span className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400">{t('login.magic.or')}</span>
                  <span className="h-px flex-1 bg-slate-200" />
                </div>

                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  onClick={() => {
                    setError(null)
                    setMode('magic')
                  }}
                >
                  {t('login.magic.cta')}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-2">
                  {t('login.accessInvitationOnly')}
                </p>
              </form>
            )}

            {mode === 'magic' && (
              <form className="space-y-4" onSubmit={handleMagicRequest}>
                <p className="text-sm text-slate-600">{t('login.magic.explain')}</p>
                <Input
                  id="magicEmail"
                  label={t('login.workEmail')}
                  type="email"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  required
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={magicSending}
                >
                  {magicSending ? t('login.magic.sending') : t('login.magic.send')}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setError(null)
                    setMode('password')
                  }}
                >
                  {t('login.magic.backToPassword')}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-2">
                  {t('login.accessInvitationOnly')}
                </p>
              </form>
            )}

            {mode === 'sent' && (
              <div className="space-y-4">
                <p className="text-base font-semibold text-slate-900">{t('login.magic.sentTitle')}</p>
                <p className="text-sm text-slate-600">
                  {t('login.magic.sentBody', { minutes: sentMinutes })}
                </p>
                <p className="text-xs text-slate-500">{t('login.magic.spamHint')}</p>

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={resendIn > 0 || magicSending}
                    onClick={() => void requestMagicLink()}
                  >
                    {magicSending ? t('login.magic.sending') : t('login.magic.resend')}
                  </Button>
                  {resendIn > 0 && (
                    <span className="text-xs text-slate-500">
                      {t('login.magic.resendIn', { seconds: resendIn })}
                    </span>
                  )}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setError(null)
                    setMode('password')
                  }}
                >
                  {t('login.magic.backToPassword')}
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}
